import { GLOBAL_PROFILE } from 'components/Root.loader'
import { BUILTIN_DOC_TYPES, VERIFY_STATUS_NO } from 'config'
import {
  DocListItem,
  Document,
  SERVICE_ID,
  DocumentJSON,
  VerifyData,
} from 'entities/Documents'
import { DocumentGeneral, DocumentGeneralJSON } from 'entities/DocumentsGeneral'
import {
  DocumentInvoiceV2,
  DocumentInvoiceV2JSON,
} from 'entities/DocumentsInvoiceV2'
import {
  DocumentBuilder,
  DocumentBuilderJSON,
} from 'entities/DocumentsOcrBuilder'
import {
  DocumentOrderForm,
  DocumentOrderFormJSON,
} from 'entities/DocumentsOrderForm'
import qs from 'qs'
import { FileImportType } from 'types'

import { getObj2, sendDelete, post, postMultipart, put, get } from './fetch'

const docsParams = (argParams, isVerify) => {
  const params = { ...argParams }
  const { updateDateFrom, updateDateTo, docTypeName, sort } = params
  if (updateDateFrom) {
    params.updateDateFrom = new Date(`${updateDateFrom}T00:00:00`).toISOString()
  }

  if (updateDateTo) {
    params.updateDateTo = new Date(`${updateDateTo}T23:59:59`).toISOString()
  }

  if (sort) {
    // ordering of schedule_doc_deletion equals update_time
    // but schedule_doc_deletion is not real data column
    params.sort = sort.replace('schedule_doc_deletion', 'update_time')
  }

  if (BUILTIN_DOC_TYPES.includes(docTypeName)) {
    // API only accept docTypeName for OCR Builder type
    params.docType = docTypeName
    params.docTypeName = ''
  }

  params.groupType = isVerify ? 'verify' : 'normal'
  return params
}

export async function fetchNormalDocuments(params) {
  return innerFetchDocuments(params, false, false)
}

export async function fetchDeleted(params, isVerify = false) {
  return innerFetchDocuments(params, isVerify, true)
}

async function innerFetchDocuments(params, isVerify, deleted = false) {
  const api = deleted ? 'documents/deleted' : 'documents'
  return await getObj2<{ docInfoList: DocListItem[]; total: number }>(api, {
    params: docsParams(params, isVerify),
    paramsSerializer: (prs) => {
      return qs.stringify(prs, { arrayFormat: 'repeat' })
    },
    cache: {
      ttl: 1000,
    },
  })
}

export async function fetchTasks(params) {
  return await getObj2<
    {
      docInfoList: DocListItem[]
      type: number
      total: number
      perPage: number
    }[]
  >('documents/myTask', {
    params: docsParams(params, true),
    cache: { ttl: 1000 },
  })
}

const getDocumentObject = (obj: DocumentJSON) => {
  switch (obj.docInfo.ocrService.ocrServiceId) {
    case SERVICE_ID.GENERAL_OCR:
      return new DocumentGeneral(obj as DocumentGeneralJSON)
    case SERVICE_ID.OCR_BUILDER:
      return new DocumentBuilder(obj as DocumentBuilderJSON)
    case SERVICE_ID.ORDER_FORM:
      return new DocumentOrderForm(obj as DocumentOrderFormJSON)
    case SERVICE_ID.INVOICEV2:
      return new DocumentInvoiceV2(obj as DocumentInvoiceV2JSON)
  }
}

export async function getDetail(id: string): Promise<Document> {
  const obj = await getObj2<DocumentJSON>(`documents/${id}`)
  const rtn: Document | undefined = getDocumentObject(obj)
  if (rtn) {
    if (
      obj.docInfo.isVerify &&
      obj.docInfo.documentStatusNo === VERIFY_STATUS_NO &&
      GLOBAL_PROFILE.isManager
    ) {
      obj.verifyData = await getVerify(id)
    }
    return rtn
  }
  throw Error(
    `unknown document OCR service id: ${obj.docInfo.ocrService.ocrServiceId}`,
  )
}

export function getVerify(objectID: string) {
  return get(`documents/${objectID}/verify`) as Promise<VerifyData>
}

export interface UploadPayload {
  docName: string
  docType: string
  fileExt: string
  subGroup: string
  importType: FileImportType
  file: File
  ocrBuilderConfigID?: number
}

export interface UploadDocResponseData {
  status: number
  createdAt: string
  jobId?: string
}

export async function uploadDocument(
  data: UploadPayload,
): Promise<UploadDocResponseData> {
  const formData = new FormData()
  Object.entries(data).forEach(([key, val]) => {
    formData.append(key, val)
  })

  const res = await postMultipart('documents', formData)
  return res.data
}

// 論理削除
export function softDeleteDocument(objectIDList) {
  return sendDelete('documents', {
    objectIDList,
  })
}

// 物理削除
export function hardDeleteDocument(objectIDList) {
  return sendDelete('documents/deleted', {
    objectIDList,
  })
}

export async function updateDocument(
  objectID: string,
  currentStatusNo: number,
  document: unknown,
) {
  const res = await put(`documents/${objectID}`, {
    objectID,
    currentStatusNo,
    document,
  })
  return res
}

export async function updatePartialDocument(objectID, partial) {
  return await put(`documents/${objectID}/partial`, partial)
}

interface SendResponse {
  errorFlag: boolean
  results: {
    objectID: string
    status: number
    message?: string
    errResBody?: string
  }[]
}

export function sendDocument(objectIDList: string[]) {
  return post<SendResponse>('documents/send', { objectIDList })
}

export function restoreDocument(objectIDList: string[]) {
  return post('documents/untrash', { objectIDList })
}

interface AssignUserOptions {
  objectIDs: string[]
  assigneeUserID: number
}

export function assignUser({ objectIDs, assigneeUserID }: AssignUserOptions) {
  return put('documents/assignees', { objectIDs, assigneeUserID })
}

export async function confirmDocument(objectID: string, document: unknown) {
  const res = await put(`documents/${objectID}/confirm`, {
    objectID,
    document,
  })
  return res
}

export async function verifyDocument(objectID: string, document: unknown) {
  const res = await put(`documents/${objectID}/verify`, {
    objectID,
    document,
  })
  return res
}
